import React from 'react';
import { Box, Typography, Grid as MuiGrid, Paper, useTheme, List, ListItem, ListItemIcon, ListItemText, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, useMediaQuery } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination, Grid as SwiperGrid } from 'swiper/modules';
import 'swiper/css/grid';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import DownloadIcon from '@mui/icons-material/Download';
import { motion } from 'framer-motion';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import './GrowthStrategy.css';

const MotionBox = motion(Box);
const MotionPaper = motion(Paper);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => {
  const theme = useTheme();

  
  return (
    <MotionPaper

      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      elevation={0}
      sx={{
        pt: 4,
        pb: { xs: 4, sm: 6, md: 8 },
        mt: 4,
        mb: { xs: 4, sm: 6, md: 8 },
        backgroundColor: 'rgba(63, 160, 255, 0.03)',
        borderRadius: 2,
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(63, 160, 255, 0.15)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 1,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%),
            linear-gradient(-45deg, transparent 48%, rgba(63, 160, 255, 0.08) 50%, transparent 52%)
          `,
          backgroundSize: '60px 60px',
          opacity: 0.8,
          zIndex: -2
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '150%',
          height: '150%',
          background: 'radial-gradient(circle at center, rgba(63, 160, 255, 0.06) 0%, transparent 70%)',
          opacity: 0.7,
          zIndex: -1
        }
      }}
    >
      <Typography
        variant="h4"
        component="h3"
        sx={{
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          fontWeight: 800,
          mb: { xs: 3, sm: 4 },
          background: 'linear-gradient(135deg, #000 0%, #333 50%, #000 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textAlign: 'center',
          letterSpacing: '0.05em',
          position: 'relative',
          zIndex: 2,
          textTransform: 'uppercase',
          '&::after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(63, 160, 255, 0.5), transparent)',
            margin: '16px auto 0'
          }
        }}
      >
        {title}
      </Typography>
      <Box sx={{
        position: 'relative',
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, sm: 3, md: 4 }
      }}>
        <Box>
          {children}
        </Box>
      </Box>

    </MotionPaper>
  );
};

const GrowthStrategy = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.up('md'));

  const getSlidesPerView = () => {
    if (isLargeScreen) return 4;
    if (isMediumScreen) return 3;
    return 1;
  };
  
  const handleDownload = () => {
    try {
      const link = document.createElement('a');
      link.href = '/NEXTBASKET_Growth_Strategy.pdf';
      link.download = 'NEXTBASKET_Growth_Strategy.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  return (
    <Box sx={{ mt: { xs: 4, sm: 6, md: 8 } }}>
      <Typography
        variant="h3"
        component="h2"
        align="center"
        sx={{
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
          fontWeight: 700,
          mb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 70%, #1a4b80 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          textTransform: 'uppercase',
          '&::after': {
            content: '""',
            display: 'block',
            width: '60px',
            height: '4px',
            backgroundColor: theme.palette.primary.main,
            margin: '16px auto 0',
            borderRadius: '2px'
          }
        }}
      >
        NEXT BASKET Growth Strategy
      </Typography>
      <Typography
        variant="subtitle2"
        sx={{
          textAlign: 'center',
          color: 'text.secondary',
          fontSize: { xs: '0.85rem', sm: '0.9rem' },
          opacity: 0.8,
          mb: { xs: 3, sm: 4, md: 5 }
        }}
      >
        5 slides available
      </Typography>

      <Typography
        variant="subtitle1"
        align="center"
        sx={{
          mb: 5,
          color: 'text.secondary',
          fontStyle: 'italic'
        }}
      >
        
      </Typography>

      <Typography
        variant="subtitle1"
        sx={{
          textAlign: 'center',
          mb: 2,
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '& svg': {
            animation: 'slideHint 1.5s ease-in-out infinite',
            '@keyframes slideHint': {
              '0%, 100%': { transform: 'translateX(0)' },
              '50%': { transform: 'translateX(5px)' }
            }
          }
        }}
      >
        Swipe or use arrows for more content
        <ArrowRightIcon sx={{ fontSize: 17 }} />
      </Typography>

      <Box sx={{ mx: { xs: -2, md: 0 }, position: 'relative' }}>
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={24}
          slidesPerView={1}
          centeredSlides
          effect={'slide'}
          autoHeight={true}
          updateOnWindowResize={true}
          observer={true}
          observeParents={true}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          pagination={{
            clickable: true,
            dynamicBullets: true
          }}
          style={{ 
            padding: isMobile ? '20px 10px 20px' : '20px 40px',
          }}
          className="growth-strategy-swiper swiper-container"
          onSlideChange={(swiper: SwiperType) => {
            const swiperContainer = swiper.el.closest('.growth-strategy-swiper');
            if (swiperContainer) {
              swiperContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
          breakpoints={{
            0: {
              slidesPerView: 1,
              spaceBetween: 16
            },
            768: {
              slidesPerView: 1,
              spaceBetween: 24
            }
          }}
          >
            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="WHAT IS NEXT BASKET">
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: 'text.secondary',
                      fontStyle: 'italic',
                      mb: 2
                    }}
                  >
                    Prepared by Borislav Malinov
                    <br />
                    Founder and Visionary Endorsed by the Board of Directors
                  </Typography>

                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    NEXT BASKET is a 360-degree business model with innovative 5-in-1 e-commerce solutions
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            AI-based software platform for creating online stores
                          </Typography>
                        }
                        secondary="An innovative e-commerce platform built global best practices. With built-in AI model at the core"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Integrated ERP system in the NEXT BASKET platform
                          </Typography>
                        }
                        secondary="It has all the necessary functions to manage business processes"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Fulfilment warehouse software
                          </Typography>
                        }
                        secondary="Designed for warehouses that place online orders"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Owned and rented storage space
                          </Typography>
                        }
                        secondary="Fulfilment warehouses in different countries around the world in order to work with D2C business models"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Provision of multiple e-commerce services
                          </Typography>
                        }
                        secondary="112+ services, some of which are free and others paid"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="WHAT ARE OUR VALUES">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            The customer is at the centre
                          </Typography>
                        }
                        secondary="It's their wants and needs that are important, not ours. If we are oriented to the needs of customers and manage to segment them correctly, our success is certain"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            We promise only doable things and never mislead the client's trust
                          </Typography>
                        }
                        secondary="We always keep our promises"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            We show flexibility and change when necessary
                          </Typography>
                        }
                        secondary="Technology evolves, so do we. Consumers change, we follow them"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Innovation is an integral part of our working style
                          </Typography>
                        }
                        secondary="Only in this way can we be better than the competitors"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            We are an employer that cares about the development of its employees
                          </Typography>
                        }
                        secondary="Trainings, seminars, special programs for creating a TOP team"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="BUSINESS MODELS">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {[
                      {
                        title: '1. SELF',
                        subtitle: 'SaaS model, price from 10 to 44 dollars per month',
                        description: 'SaaS model+ e-commerce services. This is a typical SaaS model where the Partner pays a monthly or annual subscription. But unlike our competitors, we additionally offer warehousing, marketing and sales services at the Partner\'s request.'
                      },
                      {
                        title: '2. LOCAL',
                        subtitle: 'Partner pays 7% of revenue',
                        description: 'Create a new online store to an existing physical store. Applicable to physical sites that do not have an online presence. The goods in the site become stock for the online store. We install our ERP system in the Partner\'s store or connect to their ERP system, thus making the connection between the two stores'
                      },
                      {
                        title: '3. DROPSHIPPING',
                        subtitle: 'SaaS model, price from 10 to 44 dollars per month',
                        description: 'D2C (Direct to Consumer) - a unique dropshipping model designed for small retailers. The NEXT BASKET platform is pre-tethered with dropshipping platforms like Ali Express. Thanks to this, fully finished online stores are created with products from different categories in them (for pets, office accessories, household appliances, etc.). These stores are sold to merchants who can immediately start working with them'
                      },
                      {
                        title: '4. GLOBAL',
                        subtitle: 'Partner pays 7% of revenue',
                        description: 'NEXT BASKET is connected by software to DHL fullfillment warehouses in Europe. Manufacturers from Asia deliver the goods to these warehouses and NEXT BASKET sets up online stores for 28 European countries and the Partner starts sales in them. The management of the entire online business is done by NEXT BASKET. This makes it easy for manufacturers to set foot on another continent. This second D2C method has a working analogue in Indonesia for European manufacturers, but is not yet applied in other countries'
                      },
                      {
                        title: '5. NB PRO',
                        subtitle: 'Partner pays 1%-3% of revenue',
                        description: 'Creating online stores for large online retailers. The NEXT BASKET platform is designed for high load with multiple products and high traffic, in one with exceptional stability. This allows its application for online stores with high turnover'
                      },
                      {
                        title: '6. Business without employees',
                        subtitle: 'Partner pays 7% of revenue',
                        description: 'Establishing a link between NEXT BASKET\'s fulfilment warehouse and online store. The partner only delivers the products to the warehouse, and NEXT BASKET manages its online business for 7% of the revenue.'
                      },
                      {
                        title: '7. B2B platform',
                        subtitle: 'Partner pays a monthly fee',
                        description: 'Selling the Platform as a B2B solution for distributors and manufacturers who sell to retail outlets. Currently there is no serious B2B product on the market for mass use'
                      },
                      {
                        title: '8. ERP NEXT BASKET',
                        subtitle: 'Initial installation fee, then user fee',
                        description: 'Installation of our ERP system only (without the online platform) for anyone looking for a more functional modern ERP solution. This applies to both NB Partners and external companies'
                      }
                    ].map((model, index) => (
                      <MotionPaper
                        key={index}
                        elevation={0}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        sx={{
                          p: 3,
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {model.title}
                        </Typography>
                        <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 500 }}>
                          {model.subtitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                          {model.description}
                        </Typography>
                      </MotionPaper>
                    ))}
                  </Box>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="HOW WE WILL TAKE MARKET SHARE FROM SHOPFAIR IN THE U.S.">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            With reduced costs to Partners, online store owners
                          </Typography>
                        }
                        secondary="The final monthly price for them will be 30% lower with the same features of the online stores"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Shopify is a software platform and we are an all-in-one model
                          </Typography>
                        }
                        secondary="NEXT BASKET offers multiple services together with the online shop, this makes us the preferred choice for those clients who need help and assistance"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Personalisation
                          </Typography>
                        }
                        secondary="Online stores from NEXT BASKET can be created to fulfill all the wishes of Partners"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="WHAT ARE OUR GOALS">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            D2C dropshipping model, unique worldwide, to become the leading dropshipping method for e-commerce
                          </Typography>
                        }
                        secondary="Winning small retailers with 100% online store ready sales"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            To develop our second unique D2C business model GLOBAL
                          </Typography>
                        }
                        secondary="It is designed for producers who want to sell in other continents"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            To become the dominant online shopping platform worldwide in those business models with which we are unrivalled
                          </Typography>
                        }
                        secondary="Applicable to Dropshipping, Business Without Employees, GLOBAL and NB PRO business models"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            To be the preferred software for NB PRO and NB B2B by major online retailers
                          </Typography>
                        }
                        secondary="Very high demand expected for high-performance eCommerce software"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            To invest in our own fullfillment warehouses
                          </Typography>
                        }
                        secondary="Thus closing the circle in the NB 360˚ model"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Becoming a unicorn
                          </Typography>
                        }
                        secondary="The goal is to take a leading market share in those business models where we have no competition"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Happy shareholders
                          </Typography>
                        }
                        secondary="Earned return on investment minimum 20% per annum"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="HOW WE WILL ACHIEVE OUR GOALS">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Create a platform based on best practices and the latest technologies
                          </Typography>
                        }
                        secondary="We create trends instead of just following them"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Continuous improvement of business models
                          </Typography>
                        }
                        secondary="We have created an innovative method that needs constant monitoring"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Constant implementation of new features, integrations and innovations
                          </Typography>
                        }
                        secondary="We are agile and proactive, allowing us to move ahead of market needs, not behind them"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Building a team of top professionals
                          </Typography>
                        }
                        secondary="Attracting motivated, cohesive, responsible and well-trained employees"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            We learn from our mistakes
                          </Typography>
                        }
                        secondary="Each error is analysed to eliminate the risk of its recurrence"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            We are flexible
                          </Typography>
                        }
                        secondary="We are constantly changing, learning and adapting"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="WHAT BUSINESS NEEDS WE SOLVE">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            A lot of people want to but can't start an online business because they don't know how
                          </Typography>
                        }
                        secondary="NEXT BASKET offers an easy solution - the DROPSHIPPING and Business without employees options"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Existing online merchants want a good online store but at a better price
                          </Typography>
                        }
                        secondary="NEXT BASKET provides a ready solution - the SELF option"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Many physical stores don't know that they can have additional revenue by creating an online store to their physical location
                          </Typography>
                        }
                        secondary="Here we apply the LOCAL variant"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            57% of manufacturers in Europe don't have an online store according to a survey
                          </Typography>
                        }
                        secondary="Here also the LOCAL variant is applied, in this case we integrate to the Partner's ERP"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Large online merchants have no choice from a secure and reliable eCommerce solution
                          </Typography>
                        }
                        secondary="We offer the NB PRO and NB B2B variants"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="POTENTIAL RISKS AND RISK MANAGEMENT">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Technological
                          </Typography>
                        }
                        secondary="Platform crashes, data security, integration errors. Measures: constant audits, backup servers and double backups, 24/7 monitoring."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Market
                          </Typography>
                        }
                        secondary="Aggressive competition, change in consumer behaviour. Measures: monthly competitive analysis, dynamic pricing, continuous product development."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Operational
                          </Typography>
                        }
                        secondary="Human factor, errors in storage and logistics. Measures: process automation, regular training, internal and external SLA audits"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="KEY PARTNERSHIPS (US)">
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                    {[
                      {
                        title: 'Logistics',
                        items: ['UPS', 'FedEx', 'USPS']
                      },
                      {
                        title: 'Fulfillment',
                        items: ['Amazon FBA', 'ShipBob', 'Flexport']
                      },
                      {
                        title: 'Payments',
                        items: ['PayPal', 'Stripe', 'Braintree']
                      },
                      {
                        title: 'Web3 Payments',
                        items: ['BitPay', 'Coinbase Commerce', 'Circle USDC']
                      },
                      {
                        title: 'Fintech / BNPL',
                        items: ['Affirm', 'Klarna']
                      },
                      {
                        title: 'Finance for traders',
                        items: ['Payoneer', 'Brex']
                      },
                      {
                        title: 'Abandoned trolleys',
                        items: ['Klaviyo', 'Omnisend', 'Recart (until you create your own tulle)']
                      },
                      {
                        title: 'Infrastructure',
                        items: ['Google Cloud - a hybrid model for taking peak traffic']
                      }
                    ].map((category, index) => (
                      <Box key={index}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            height: '100%',
                            backgroundColor: 'rgba(63, 160, 255, 0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(63, 160, 255, 0.2)',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(63, 160, 255, 0.08)'
                          }}
                        >
                          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.primary.main }}>
                            {category.title}
                          </Typography>
                          <List>
                            {category.items.map((item, itemIndex) => (
                              <ListItem key={itemIndex} sx={{ py: 0.5 }}>
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                  <ArrowRightIcon color="primary" sx={{ fontSize: 20 }} />
                                </ListItemIcon>
                                <ListItemText primary={item} />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="MARKETING STRATEGY - USA">
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Targeting the US market with SELF and DROPSHIPPING start-up business models
                          </Typography>
                        }
                        secondary="The remaining models are launched in phases after proof of concept."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Target groups
                          </Typography>
                        }
                        secondary="New online entrepreneurs (SELF) and small merchants active in"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Acquisition channels
                          </Typography>
                        }
                        secondary="Google Ads (search), TikTok and YouTube (video), META (retargeting), affiliate networks"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Content and branding
                          </Typography>
                        }
                        secondary="Weekly webinars 'Start Your First Online Store' and 'Profit with Dropshipping', blog articles and guest posts, UGC videos from real merchants."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Lead nurturing
                          </Typography>
                        }
                        secondary="90-day email/SMS flow (Klaviyo or Omnisend) with training, personalized demo and invitation to a free 30-day test store"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Credibility and visibility
                          </Typography>
                        }
                        secondary="Participation in 'Best eCommerce Platforms USA' rankings, PR case studies with top 10 successful stores"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            KPI year 1
                          </Typography>
                        }
                        secondary="2,000 active SELF stores, 500 active DROPSHIPPING stores, CAC ≤ $120, ROAS ≥ 4"
                      />
                    </ListItem>
                  </List>
                </Section>
              </Box>
            </SwiperSlide>

            <SwiperSlide>
              <Box sx={{ px: 2 }}>
                <Section title="GLOSSARY OF TERMS USED">
                  <TableContainer component={Paper} elevation={0} sx={{ backgroundColor: 'transparent' }}>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 600, border: 'none' }}>NB, The Platform:</TableCell>
                          <TableCell sx={{ border: 'none' }}>NEXT BASKET</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 600, border: 'none' }}>Partner, Trader:</TableCell>
                          <TableCell sx={{ border: 'none' }}>Business user of the Platform</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" sx={{ fontWeight: 600, border: 'none' }}>End customer:</TableCell>
                          <TableCell sx={{ border: 'none' }}>Consumer shopping from the online store</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Section>
              </Box>
            </SwiperSlide>

            <div className="swiper-button-prev" />
            <div className="swiper-button-next" />
            <div className="swiper-pagination" />
          </Swiper>

          {/* Download Button */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6, gap: 2, px: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontSize: { xs: '0.85rem', sm: '0.9rem' },
                opacity: 0.8,
                mb: 1
              }}
            >
            
            </Typography>
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                color: 'text.primary',
                mb: 2
              }}
            >
              Download our detailed Growth Strategy to learn more about NEXT BASKET
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                try {
                  const link = document.createElement('a');
                  link.href = '/NEXTBASKET_Growth_Strategy.pdf';
                  link.download = 'NEXTBASKET_Growth_Strategy.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (error) {
                  console.error('Error downloading file:', error);
                }
              }}
              startIcon={<DownloadIcon />}
              sx={{
                py: { xs: 1.75, md: 1.75 },
                px: { xs: 4, md: 5 },
                minWidth: { xs: 'auto', md: '300px' },
                borderRadius: 15,
                textTransform: 'none',
                fontSize: { xs: '1.2rem', md: '1.1rem' },
                fontWeight: 600,
                letterSpacing: '0.02em',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: '#fff',
                border: '1px solid rgba(63, 160, 255, 0.3)',
                backdropFilter: 'blur(8px)',
                '& .MuiSvgIcon-root': {
                  color: '#fff',
                  fontSize: '1.4rem',
                  marginRight: '8px',
                  transition: 'transform 0.3s ease'
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                  transform: 'translateX(-100%) skewX(-15deg)',
                  transition: 'transform 0.7s ease'
                },
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  boxShadow: '0 8px 25px rgba(63, 160, 255, 0.4)',
                  transform: 'translateY(-2px)',
                  border: '1px solid rgba(63, 160, 255, 0.5)',
                  '& .MuiSvgIcon-root': {
                    transform: 'scale(1.1) rotate(-10deg)'
                  },
                  '&::before': {
                    transform: 'translateX(100%) skewX(-15deg)'
                  }
                },
                '&:active': {
                  transform: 'translateY(1px)',
                  boxShadow: '0 4px 15px rgba(63, 160, 255, 0.3)'
                }
              }}
            >
              Download PDF
            </Button>
            <br /><br />
          </Box>
        </Box>

       
    </Box>
  );
};

export default GrowthStrategy;